import { ReportDefinition } from '../models/report-definition';
import { ReportRowVm } from '../models/report-row-vm';
import {
   getFinancialPostingDate,
   getFinancialPostingRoom,
   getFinancialPostingSource,
   getFinancialPostingTime,
   getFinancialPostingTransactionCode,
   getFinancialPostingUser
} from '../utils/financial-posting.utils';
import { getFirstNumber, getFirstString } from '../utils/report-field.utils';
import {
   formatCurrency,
   formatFlexibleDate,
   formatTime
} from '../utils/report-formatting.utils';
import { getRealTyallsRowId } from '../utils/report-row-id.utils';

type TaCommsRow = {
   rowId: string;
   date: string;
   dateRaw: string;
   time: string;
   roomNumber: string;
   name: string;
   confirmationNumber: string;
   reservationId: string;
   transactionCode: string;
   transactionDescription: string;
   folioWindowNo: string;
   amount: string;
   amountRaw: number;
   cashId: string;
   hasRealTyallsRowId: boolean;
};

function getRowId(row: unknown): string {
   const realTyallsRowId = getRealTyallsRowId(row);

   if (realTyallsRowId) {
      return realTyallsRowId;
   }

   return (
      getFirstString(row, ['Id', 'id', 'ReservationId', 'reservationId']) ||
      `ta-comms:${getFinancialPostingDate(
         row
      )}:${getFinancialPostingRoom(row)}:${getFirstString(row, [
         'ConfirmationNumber',
         'confirmationNumber'
      ])}:${getFinancialPostingTransactionCode(row)}`
   );
}

function getTransactionDescription(row: unknown): string {
   const transactionCode = getFinancialPostingTransactionCode(row);

   return (
      getFirstString(row, [
         'TransactionCodeDescription',
         'transactionCodeDescription',
         'TransactionDescription',
         'transactionDescription',
         'Description',
         'description'
      ]) || transactionCode
   );
}

function getAmount(row: unknown): number {
   const credit = getFirstNumber(row, ['Credit', 'credit']);

   if (credit !== null) {
      return credit;
   }

   const debit = getFirstNumber(row, ['Debit', 'debit']);

   if (debit !== null) {
      return debit;
   }

   const amount = getFirstNumber(row, ['Amount', 'amount', 'AmountRaw', 'amountRaw']);

   return amount ?? 0;
}

function normaliseRows(reportJson: unknown): TaCommsRow[] {
   const { rows: sourceRows } = getFinancialPostingSource(reportJson, [
      'TaComms',
      'TAComms'
   ]);

   return sourceRows
      .map((row) => {
         const dateRaw = getFinancialPostingDate(row);
         const amountRaw = getAmount(row);

         return {
            rowId: getRowId(row),
            date: formatFlexibleDate(dateRaw),
            dateRaw,
            time: formatTime(getFinancialPostingTime(row)),
            roomNumber: getFinancialPostingRoom(row),
            name: getFirstString(row, ['Name', 'name', 'GuestName', 'guestName']),
            confirmationNumber: getFirstString(row, [
               'ConfirmationNumber',
               'confirmationNumber'
            ]),
            reservationId: getFirstString(row, ['ReservationId', 'reservationId']),
            transactionCode: getFinancialPostingTransactionCode(row),
            transactionDescription: getTransactionDescription(row),
            folioWindowNo: getFirstString(row, ['FolioWindowNo', 'folioWindowNo']),
            amount: formatCurrency(amountRaw),
            amountRaw,
            cashId: getFinancialPostingUser(row, ['CashId', 'cashId'], []),
            hasRealTyallsRowId: !!getRealTyallsRowId(row)
         };
      })
      .filter(
         (row) =>
            row.roomNumber ||
            row.name ||
            row.confirmationNumber ||
            row.amountRaw !== 0
      );
}

function sortRows(rows: TaCommsRow[]): TaCommsRow[] {
   return [...rows].sort((a, b) => {
      const dateCompare = a.dateRaw.localeCompare(b.dateRaw);

      if (dateCompare !== 0) {
         return dateCompare;
      }

      const aRoom = Number(a.roomNumber);
      const bRoom = Number(b.roomNumber);

      if (Number.isFinite(aRoom) && Number.isFinite(bRoom)) {
         return aRoom - bRoom;
      }

      return a.roomNumber.localeCompare(b.roomNumber);
   });
}

export const TA_COMMS_DEFINITION: ReportDefinition = {
   reportType: 'TaComms',
   version: 2,
   title: 'TA Comms',

   sorting: {
      enabled: true,
      persist: true
   },

   header: {
      title: 'TA Comms',
      snippets: ({ allRows }) => {
         const dataRows = allRows.filter(
            (row) => row.cells?.['rowType'] === 'taComms'
         );

         const totalAmount = dataRows.reduce((total, row) => {
            const amount = Number(row.cells?.['amountRaw']);

            return Number.isFinite(amount) ? total + amount : total;
         }, 0);

         return [
            {
               label: 'Count',
               value: dataRows.length
            },
            {
               label: 'Total Comms',
               value: formatCurrency(totalAmount),
               tone: totalAmount < 0 ? 'danger' : 'neutral'
            }
         ];
      }
   },

   rowChecks: {
      enabled: false,
      includeNestedRows: false,
      selectableRowTypes: ['taComms']
   },

   annotations: {
      enabled: true,
      selectableRowTypes: ['taComms'],
      disabledMessage:
         'Row comments and row highlighting are not available for this report layout. Please use the main report comments panel to add comments for this report.'
   },

   columns: [
      {
         key: 'date',
         header: 'TRN DATE',
         width: '105px',
         sort: {
            type: 'date',
            getValue: (row) => row.cells['dateRaw']
         }
      },
      {
         key: 'time',
         header: 'TIME',
         width: '75px',
         sort: { type: 'string' }
      },
      {
         key: 'roomNumber',
         header: 'ROOM',
         width: '90px',
         sort: { type: 'string' }
      },
      {
         key: 'name',
         header: 'GUEST NAME',
         width: '190px',
         maxChars: 80,
         sort: { type: 'string' }
      },
      {
         key: 'confirmationNumber',
         header: 'CONFIRMATION',
         width: '150px',
         sort: { type: 'string' }
      },
      {
         key: 'transactionCode',
         header: 'TRN CODE',
         width: '100px',
         sort: { type: 'string' }
      },
      {
         key: 'transactionDescription',
         header: 'DESCRIPTION',
         width: '220px',
         maxChars: 90,
         sort: { type: 'string' }
      },
      {
         key: 'folioWindowNo',
         header: 'FOLIO',
         width: '80px',
         sort: { type: 'string' }
      },
      {
         key: 'amount',
         header: 'AMOUNT',
         width: '120px',
         sort: {
            type: 'number',
            getValue: (row) => row.cells['amountRaw'],
            defaultDirection: 'desc'
         }
      },
      {
         key: 'cashId',
         header: 'CASH ID',
         width: '120px',
         sort: { type: 'string' }
      }
   ],

   filter: {
      fields: [
         {
            key: 'date',
            label: 'Transaction Date',
            type: 'date',
            getValue: (row) => row.cells['dateRaw']
         },
         { key: 'time', label: 'Time', type: 'string' },
         { key: 'roomNumber', label: 'Room', type: 'string' },
         { key: 'name', label: 'Guest Name', type: 'string' },
         {
            key: 'confirmationNumber',
            label: 'Confirmation Number',
            type: 'string'
         },
         {
            key: 'transactionCode',
            label: 'Transaction Code',
            type: 'string'
         },
         {
            key: 'transactionDescription',
            label: 'Description',
            type: 'string'
         },
         {
            key: 'folioWindowNo',
            label: 'Folio Window',
            type: 'string'
         },
         {
            key: 'amount',
            label: 'Amount',
            type: 'number',
            getValue: (row) => row.cells['amountRaw']
         },
         { key: 'cashId', label: 'Cash ID', type: 'string' }
      ]
   },

   flattenRows: (reportJson: unknown): ReportRowVm[] => {
      const rows = sortRows(normaliseRows(reportJson));

      return rows.map((row) => ({
         rowId: row.rowId,
         isAnnotatable: row.hasRealTyallsRowId,
         cells: {
            rowType: 'taComms',
            date: row.date,
            dateRaw: row.dateRaw,
            time: row.time,
            roomNumber: row.roomNumber,
            name: row.name,
            confirmationNumber: row.confirmationNumber,
            reservationId: row.reservationId,
            transactionCode: row.transactionCode,
            transactionDescription: row.transactionDescription,
            folioWindowNo: row.folioWindowNo,
            amount: row.amount,
            amountRaw: row.amountRaw,
            cashId: row.cashId
         }
      }));
   }
};
